/**
 * Location detail → Racks tab. All racks housed at this location
 * (`/racks?location_id=`) in a read-only DataTable: code link · name · status
 * pill · U-height. The location column is dropped (redundant in this scope).
 */
import { useNavigate } from 'react-router-dom';
import { Server } from 'lucide-react';
import type { ColumnDef } from '@vnetwork/ui/data';
import { DataTable, EmptyState, StatusPill } from '@vnetwork/ui/data';
import { useLocationRacks, type Rack } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { rackStatusView } from '../../racks/rack-mappers';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Location-scoped rack columns — no Location column (implied by the page). */
const columns: ColumnDef<Rack, unknown>[] = [
  {
    id: 'code',
    header: 'Code',
    cell: ({ row }) => (
      <TableLink to={`/capacities/racks/${row.original.id}`} title={row.original.code}>
        {row.original.code || row.original.id}
      </TableLink>
    ),
  },
  {
    id: 'name',
    header: 'Rack',
    cell: ({ row }) => (
      <span title={row.original.name} style={{ overflowWrap: 'anywhere' }}>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const view = rackStatusView(row.original.status);
      return <StatusPill tone={view.tone}>{view.label}</StatusPill>;
    },
  },
  {
    id: 'u_height',
    header: 'Height',
    cell: ({ row }) =>
      row.original.u_height ? <span>{row.original.u_height}U</span> : <span style={muted}>—</span>,
  },
];

export function LocationRacksTab({ locationId }: { locationId: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useLocationRacks(locationId);

  if (isError) return <TabError title="Failed to load racks" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={4} />;

  const rows: Rack[] = data?.results ?? [];

  return (
    <DataTable
      columns={columns}
      data={rows}
      getRowId={(r) => r.id}
      onRowClick={(r) => navigate(`/capacities/racks/${r.id}`)}
      emptyState={
        <EmptyState
          icon={<Server size={24} />}
          title="No racks"
          description="No racks are registered at this location yet."
        />
      }
    />
  );
}
